import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Square } from 'lucide-react';
import { useEffect, useState } from 'react';

import { billingApi } from '@/lib/api/endpoints';
import type { RunningTimerItem } from '@/lib/api/types';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * The running clocks.
 *
 * Always visible while anything runs — a timer someone cannot see is a timer
 * that runs all night. One row per clock, because two clients' clocks at once
 * is a supported, deliberate act, and each needs its own stop.
 *
 * Two placements of the same thing:
 *
 * - `sidebar` — pinned at the bottom of the desktop sidebar, as before.
 * - `header` — a strip directly under the phone's top bar. The sidebar is a
 *   drawer on a phone, so a clock living only there was invisible exactly
 *   where it is most often forgotten: on the device in your pocket after you
 *   left the desk. The stop button here is thumb-sized, because stopping is
 *   the one thing this strip exists for.
 */
export function TimerBar({ variant = 'sidebar' }: { variant?: 'sidebar' | 'header' }) {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['timer'],
    queryFn: () => billingApi.timer(),
    refetchInterval: 60_000,
  });

  const stop = useMutation({
    mutationFn: (taskId: string) => billingApi.stopTimer(taskId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timer'] });
      void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });

  const running = data?.entries ?? [];
  if (running.length === 0) return null;

  if (variant === 'header') {
    return (
      <div
        className="border-b border-sidebar-border bg-sidebar px-3 py-1.5 text-sidebar-content lg:hidden"
        role="status"
        aria-live="polite"
      >
        {running.map((item) => (
          <TimerRow
            key={item.entry.id}
            item={item}
            large
            onStop={() => stop.mutate(item.entry.task_id)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-1.5 border-t border-sidebar-border px-3 py-2.5">
      {running.map((item) => (
        <TimerRow key={item.entry.id} item={item} onStop={() => stop.mutate(item.entry.task_id)} />
      ))}
    </div>
  );
}

function TimerRow({
  item,
  onStop,
  large = false,
}: {
  item: RunningTimerItem;
  onStop: () => void;
  large?: boolean;
}) {
  const t = useT();
  const [elapsed, setElapsed] = useState(0);
  const startedAt = item.entry.started_at;
  useEffect(() => {
    const measure = () =>
      setElapsed(Math.max(0, Date.now() - new Date(startedAt).getTime()));
    measure();
    const timer = setInterval(measure, 1000);
    return () => clearInterval(timer);
  }, [startedAt]);

  return (
    <div className={cn('flex items-center gap-2', large && 'min-h-11 gap-3')}>
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sidebar-accent opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-sidebar-accent" />
      </span>
      <div className="min-w-0 flex-1">
        <p className={cn('truncate text-xs text-sidebar-content', large && 'text-sm')}>
          {item.task_title}
        </p>
        {item.client_name ? (
          <p className="truncate text-[11px] text-sidebar-subtle">{item.client_name}</p>
        ) : null}
      </div>
      <span
        className={cn(
          'text-sm font-medium tabular-nums text-sidebar-content',
          large && 'text-base',
        )}
      >
        {formatElapsed(elapsed)}
      </span>
      <button
        type="button"
        onClick={onStop}
        className={cn(
          'rounded-md p-1.5 text-sidebar-subtle transition-surface hover:bg-sidebar-hover hover:text-sidebar-content',
          // 44px square: the whole point of the strip is that this is easy to hit.
          large &&
            'flex h-11 w-11 items-center justify-center rounded-lg bg-sidebar-hover text-sidebar-content',
        )}
        aria-label={t('time.stop')}
      >
        <Square className={cn('h-3.5 w-3.5 fill-current', large && 'h-4 w-4')} aria-hidden />
      </button>
    </div>
  );
}

function formatElapsed(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => String(value).padStart(2, '0');
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`;
}
